import invariant from 'invariant';
import queryString from 'query-string';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { styled } from 'styled-components';

import { makeDonation } from '#/api/donations';
import type { DonationPayload } from '#/api/donations';
import { Box } from '#/components/Box/Box';
import { CmsSocialButtons } from '#/components/CmsSocialButtons/CmsSocialButtons';
import { Divider } from '#/components/Divider/Divider';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { Body } from '#/components/Text/Body';
import { RichText } from '#/components/Text/RichText';
import { useUserEnvironment } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiPage } from '#/modules/cms/api/types/CmsApiPage';
import type { CmsApiDonateBlockData } from '#/modules/cms/api/types/blocks/CmsApiDonateBlock';
import { viewDonationReceiptRoute } from '#/routing/routes';
import {
  colors,
  cssBreakpoints,
  fontWeights,
  gutterWidth,
  maxWidth,
  textStyles,
} from '#/theme/theme';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { getCookieValue } from '#/utils/cookies';
import { imageUrl } from '#/utils/filestack';
import { filterObject, mapObject } from '#/utils/functional/object';
import { scrollToTop } from '#/utils/scroll';
import { snakeCaseToCamelCase } from '#/utils/string';
import { hiDPI } from '#/utils/style';

import {
  CmsDonateBlockInternalForm,
  type DonateBlockFormValues,
} from './CmsDonateBlockInternalForm';

type DonateFormValues = DonateBlockFormValues;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const Container = styled(Box)<{
  $imageHandle: string | undefined;
  $mimetype: string | undefined;
}>`
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  ${({ $imageHandle, $mimetype }) =>
    $imageHandle &&
    `
    background-image: url('${imageUrl({
      data: { handle: $imageHandle, mimetype: $mimetype },
      width: 800,
      height: undefined,
      fit: 'max',
      insecureSVG: false,
    })}');
    ${hiDPI()}: {
      background-image: url('${imageUrl({
        data: { handle: $imageHandle, mimetype: $mimetype },
        width: 1600,
        height: undefined,
        fit: 'max',
        insecureSVG: false,
      })}');
    }
    @media all and (min-width: ${cssBreakpoints.lgUp}) {
      background-image: url('${imageUrl({
        data: { handle: $imageHandle, mimetype: $mimetype },
        width: 1280,
        height: undefined,
        fit: 'max',
        insecureSVG: false,
      })}');
      ${hiDPI()}: {
        background-image: url('${imageUrl({
          data: { handle: $imageHandle, mimetype: $mimetype },
          width: 2560,
          height: undefined,
          fit: 'max',
          insecureSVG: false,
        })}');
      }
    }
  `}
`;
const Headline = styled(
  ({
    text,
    isPageHeader,
    ...props
  }: {
    text: string;
    isPageHeader: boolean;
  }) => (
    <RichText
      dangerouslySetInnerHTML={{
        __html: `<${isPageHeader ? 'h1' : 'h2'}>${
          text ? text.replace(/<(\/?)(p|h1|h2)>/g, '<$1span>') : ''
        }</${isPageHeader ? 'h1' : 'h2'}>`,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  ),
)`
  && {
    h1,
    h2 {
      /* block-no-empty */
      ${textStyles.h2};
    }
  }
`;

function StyledSocialButtons(
  props: ComponentProps<typeof CmsSocialButtons> & { className?: string },
) {
  const { className, content, message, title, subsite } = props;
  return (
    <Box
      className={className}
      display="flex"
      my={20}
      mx="-6px"
      flexDirection="row"
      qaId="donate-thank-you-social-buttons"
    >
      <CmsSocialButtons
        content={content}
        message={message}
        title={title}
        subsite={subsite}
      />
    </Box>
  );
}

type Props = {
  data: CmsApiDonateBlockData;
  isPageHeader?: boolean;
  content: CmsApiPage;
};

export function CmsDonateBlock({
  data: { headline, body, thankYouHeadline, thankYouBody, campaignId, bgImage },
  isPageHeader,
  content,
}: Props) {
  const { i18n } = useI18n();

  const { user, environment } = useUserEnvironment();
  const location = useLocation();
  useEffect(() => {
    trackEvent('View Donate Form', {
      amount: 0,
      campaign_id: campaignId,
    });
    setTimeout(() => trackEvent('Engaged Visit: Donation Page'), 10000);
  }, [campaignId]);
  const [invoiceId, setInvoiceId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedAmount, setSubmittedAmount] = useState(25);

  const onSubmit: FormSubmitFunction<DonateFormValues> = ({
    handleError,
    updateSubmitting,
    values,
  }) => {
    const extraParamsCookieRaw = getCookieValue('extraParams');
    let extraParamsFromCookie: Record<string, unknown> = {};
    if (extraParamsCookieRaw) {
      try {
        extraParamsFromCookie = JSON.parse(extraParamsCookieRaw);
      } catch {
        extraParamsFromCookie = {};
      }
    }
    const parsedSearch = queryString.parse(location.search);
    const extraParamsFromSearch = filterObject(
      parsedSearch as Record<string, string | string[] | null>,
      (k) => k.startsWith('utm') || k === 'sf_campaign_id',
    );
    const extraParams = mapObject(
      { ...extraParamsFromCookie, ...extraParamsFromSearch },
      (k, v) => [snakeCaseToCamelCase(k.toString()), v],
    );

    const amountString =
      values.amount === 'OTHER'
        ? (values.otherAmount ?? '')
        : (values.amount?.toString() ?? '');
    const amountInteger = parseInt(amountString, 10) || 0;

    const payload: DonationPayload = {
      ...(values as DonationPayload),
      amount: amountString,
      sfCampaignId: campaignId ? campaignId.toString() : undefined,
      ...extraParams,
    };

    makeDonation(payload)
      .then((result) => {
        if (result.success) {
          trackEvent('Success: Created Donation', {
            amount: amountInteger,
            campaign_id: campaignId,
          });
          invariant(result.invoiceIds, 'Missing invoiceIds');
          invariant(result.invoiceIds.length > 0, 'Missing invoiceIds');
          setInvoiceId(result.invoiceIds[0]);
          setSubmitted(true);
          setSubmittedAmount(amountInteger);
        } else {
          invariant(result.error, 'missing error');
          // Convert Stripe error to general form error shape
          const error = {
            responseJson: {
              status: 'error' as const,
              errors: [
                {
                  location: 'general',
                  name: 'general',
                  type: result.error.stripeCode,
                  description: result.errorHuman ?? '',
                  data: {},
                },
              ],
            },
          };
          trackEvent('Error: Processing Payment', {
            amount: values.amount,
            campaign_id: campaignId,
            error_type: result.error.stripeCode,
          });
          handleError(error);
        }

        scrollToTop();
        updateSubmitting(false);
      })
      .catch((e) => {
        const errorResponse: {
          errors?: {
            name: string;
            code?: string;
            description?: string;
            data?: Record<string, unknown>;
          }[];
        } = e.responseJson;
        const { errors: errorArray } = errorResponse;
        scrollToTop();

        if (errorArray) {
          const errors = errorArray.reduce<
            Record<
              string,
              {
                name: string;
                code?: string;
                description?: string;
                data: Record<string, unknown>;
              }
            >
          >((acc, { name, code, description, data }) => {
            if (name === 'general') return acc;
            const normalizedData = isRecord(data) ? data : {};
            // eslint-disable-next-line no-param-reassign
            acc[name] = {
              name,
              code,
              description,
              data: normalizedData,
            };
            return acc;
          }, {});
          let requiredErrorMatched = false;
          Object.keys(errors).map((field) => {
            const errorForField = errors[field];
            if (errorForField?.code) {
              trackEvent('Error: Submitting Donation', {
                amount: values.amount,
                error_type: errorForField.code,
                campaign_id: campaignId,
              });
            } else if (
              errorForField?.description === 'Required' &&
              !requiredErrorMatched
            ) {
              trackEvent('Error: Submitting Donation', {
                amount: values.amount,
                error_type: 'required_fields_missing',
                campaign_id: campaignId,
              });
              requiredErrorMatched = true;
            }

            return true;
          });
        }

        handleError(e);
        updateSubmitting(false);
      });
  };

  const initialValues = user
    ? {
        amount: '',
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        country: user.location.countryCode,
      }
    : {
        country: environment?.guessedLocation.countryCode ?? 'US',
      };
  return (
    <div data-qa-id="donate">
      <Container
        $imageHandle={bgImage?.handle}
        $mimetype={bgImage?.mimetype}
        minHeight={['100%', null, null, 'calc(100vh - 123px)']}
      >
        <Box
          mx="auto"
          p={[0, null, gutterWidth / 2, gutterWidth]}
          maxWidth={maxWidth[3]}
          width={1}
        >
          <div>
            <Box
              width={['100%', null, null, `calc(50% + ${gutterWidth * 2}px)`]}
              p={[gutterWidth / 2, null, null, gutterWidth]}
              borderRadius={[0, 0, 12]}
              bg="white"
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
            >
              {submitted ? (
                <Box
                  qaId="donate-thank-you"
                  display={['block', null, 'flex']}
                  width="100%"
                >
                  <Box
                    display="flex"
                    flex="2"
                    flexDirection="column"
                    pr={gutterWidth / 2}
                  >
                    <Box
                      bg={colors.inputGrey}
                      color={colors.brandBlue}
                      borderRadius={15}
                      fontWeight={fontWeights.semiBold}
                      fontSize={[
                        (() => {
                          if (submittedAmount > 9999) return 48;
                          if (submittedAmount > 999) return 64;
                          return 80;
                        })(),
                      ]}
                      py={40}
                      px={20}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      qaId="donate-thank-you-submitted-amount"
                    >
                      {`$${submittedAmount} USD`}
                    </Box>
                    <StyledSocialButtons
                      content={content}
                      message={content.metaDescription}
                      title={content.title}
                      subsite={content.subsite.publicName}
                    />
                    <StyledNativeLinkWithRef
                      qaId="donate-thank-you-receipt-link"
                      href={viewDonationReceiptRoute.with({ invoiceId })}
                      target="donate_thank_you_receipt"
                    >
                      <Body textAlign="center">{i18n('View Receipt')}</Body>
                    </StyledNativeLinkWithRef>
                  </Box>
                  <Box
                    flex="3"
                    pl={[0, null, gutterWidth / 2]}
                    mt={[gutterWidth, null, 0]}
                  >
                    {thankYouHeadline && (
                      <Headline
                        text={thankYouHeadline}
                        // @ts-expect-error TS(2769): No overload matches this call.
                        isPageHeader={isPageHeader}
                      />
                    )}
                    {thankYouBody && (
                      <Body
                        dangerouslySetInnerHTML={{
                          __html: thankYouBody,
                        }}
                        mt={12}
                      />
                    )}
                  </Box>
                </Box>
              ) : (
                <>
                  {headline && (
                    <Headline
                      text={headline}
                      // @ts-expect-error TS(2769): No overload matches this call.
                      isPageHeader={isPageHeader}
                    />
                  )}
                  {body && (
                    <Body
                      dangerouslySetInnerHTML={{
                        __html: body,
                      }}
                      mt={12}
                    />
                  )}

                  <Box my={[gutterWidth / 2, null, null, gutterWidth]}>
                    <Divider marginTop={0} marginBottom={0} />
                  </Box>

                  <CmsDonateBlockInternalForm
                    onSubmit={onSubmit}
                    campaignId={campaignId}
                    initialValues={initialValues}
                  />
                </>
              )}
            </Box>
          </div>
        </Box>
      </Container>
    </div>
  );
}
